import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Tiny, reusable pagination component used across admin list pages.
 *
 * Props:
 *   total       — total filtered row count
 *   page        — current 1-indexed page
 *   pageSize    — rows per page (default 12)
 *   onChange    — (newPage: number) => void
 *   testIdPrefix— optional, for data-testid hooks
 *
 * Renders nothing when there is only one page.
 */
export default function Pagination({ total, page, pageSize = 12, onChange, testIdPrefix = "pg" }) {
  const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize));
  if (totalPages <= 1) return null;

  // Build the page-number list with smart truncation: 1 … 4 5 [6] 7 8 … 20
  const pages = [];
  const window = 1; // pages on each side of current
  const push = (v) => pages.push(v);
  push(1);
  if (page - window > 2) push("…");
  for (let p = Math.max(2, page - window); p <= Math.min(totalPages - 1, page + window); p++) push(p);
  if (page + window < totalPages - 1) push("…");
  if (totalPages > 1) push(totalPages);

  const go = (p) => {
    if (typeof p !== "number") return;
    if (p < 1 || p > totalPages || p === page) return;
    onChange(p);
  };

  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  return (
    <div className="flex items-center justify-between flex-wrap gap-3 px-4 py-3 border-t border-gray-200 bg-gray-50/50">
      <div className="text-[12px] text-gray-500" data-testid={`${testIdPrefix}-info`}>
        Showing <span className="font-semibold text-gray-800">{start}</span>–
        <span className="font-semibold text-gray-800">{end}</span> of{" "}
        <span className="font-semibold text-gray-800">{total}</span>
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => go(page - 1)}
          disabled={page <= 1}
          className="h-8 w-8 inline-flex items-center justify-center text-gray-600 hover:text-emerald-700 hover:bg-emerald-50 border border-gray-200 rounded-sm disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid={`${testIdPrefix}-prev`}
          title="Previous page"
        >
          <ChevronLeft size={14} />
        </button>
        {pages.map((p, i) =>
          p === "…" ? (
            <span key={`e${i}`} className="px-2 text-gray-400 text-[12px] select-none">…</span>
          ) : (
            <button
              key={p}
              onClick={() => go(p)}
              className={`h-8 min-w-[2rem] px-2 text-[12px] font-semibold rounded-sm border ${
                p === page
                  ? "bg-emerald-600 text-white border-emerald-600"
                  : "bg-white text-gray-700 border-gray-200 hover:border-emerald-300 hover:text-emerald-700"
              }`}
              data-testid={`${testIdPrefix}-page-${p}`}
            >
              {p}
            </button>
          )
        )}
        <button
          onClick={() => go(page + 1)}
          disabled={page >= totalPages}
          className="h-8 w-8 inline-flex items-center justify-center text-gray-600 hover:text-emerald-700 hover:bg-emerald-50 border border-gray-200 rounded-sm disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid={`${testIdPrefix}-next`}
          title="Next page"
        >
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}
